import axios from "axios";
import { env } from "@/config/env";
import { endpoints } from "./endpoints";
import type { ApiEnvelope, LoginSuccessData, TokenRefreshData } from "@/types/api";
import { ApiError } from "@/services/http/errors";

const authHttp = axios.create({
  baseURL: env.apiBaseUrl,
  timeout: 11_000,
  headers: {
    "Content-Type": "application/json",
  },
});

function toApiError(error: unknown, fallback: string) {
  if (axios.isAxiosError(error)) {
    return new ApiError(
      error.response?.status ?? 0,
      error.response?.data,
      fallback,
    );
  }
  if (error instanceof ApiError) return error;
  return new ApiError(0, null, fallback);
}

export type LoginVoipFields = {
  fcm_token?: string;
  device_id?: string;
  platform?: string;
  token_type?: string;
  sip_username?: string;
};

export async function loginWithCredentials(
  email: string,
  password: string,
  voip?: LoginVoipFields,
) {
  try {
    const { data: response } = await authHttp.post<ApiEnvelope<LoginSuccessData>>(
      endpoints.login,
      {
        email: email.trim(),
        password,
        ...voip,
      },
    );

    if (!response?.success || !response.data?.access || !response.data?.refresh) {
      throw new ApiError(
        401,
        response,
        response?.message || "Login failed. Please check your credentials.",
      );
    }

    return response.data;
  } catch (error) {
    throw toApiError(error, "Login failed. Please try again.");
  }
}

export async function refreshAccessToken(refresh: string) {
  try {
    const { data: response } = await authHttp.post<TokenRefreshData>(endpoints.refresh, {
      refresh,
    });

    if (!response?.access) {
      throw new ApiError(401, response, "Session expired. Please log in again.");
    }

    return response;
  } catch (error) {
    throw toApiError(error, "Session expired. Please log in again.");
  }
}

export type LogoutBody = {
  refresh_token?: string | null;
  device_id?: string | null;
  fcm_token?: string | null;
};

export async function logoutRemote(accessToken?: string | null, body?: LogoutBody) {
  if (!accessToken) return;
  const payload: Record<string, string> = {};
  const refresh = String(body?.refresh_token || "").trim();
  const deviceId = String(body?.device_id || "").trim();
  const fcmToken = String(body?.fcm_token || "").trim();
  if (refresh) payload.refresh_token = refresh;
  if (deviceId) payload.device_id = deviceId;
  if (fcmToken) payload.fcm_token = fcmToken;
  try {
    await authHttp.post(endpoints.logout, payload, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
  } catch {
    // Local logout still proceeds even if the server call fails.
  }
}
